import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import {Injectable, Injector} from '@angular/core';
import {IExceptionTelemetry} from '@microsoft/applicationinsights-web';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {ApplicationInsightsService} from './application-insights.service';
import {ErrorService} from '../../pages/shared/errors/error.service';

/** Header to bypass this interceptor when present on a request. */
export const InterceptSkipErrorHeader = 'X-Skip-Error-Interceptor';

/**
 * Set in a request's context when its caller shows the error itself, e.g. inside a dialog the shared error prompt
 * would open behind. For the generated API clients, which take a context but no extra headers.
 */
export const SkipErrorPrompt = new HttpContextToken<boolean>(() => false);
@Injectable({
  providedIn: 'root',
})
/**
 * Captures HTTP errors, logs to Application Insights, and forwards to shared error service.
 */
export class GlobalHttpErrorInterceptorService implements HttpInterceptor {
  constructor(private injector: Injector, private errorService: ErrorService) {
  }

  // https://stackoverflow.com/a/49047764
  /**
   * Intercepts HTTP requests, optionally bypassing error handling when the skip header is present,
   * and tracks exceptions in Application Insights while surfacing them to the shared error service.
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.headers.has(InterceptSkipErrorHeader)) {
      const headers = req.headers.delete(InterceptSkipErrorHeader);
      return next.handle(req.clone({headers}));
    }
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // POST this error information to Azure App Insights
        const loggedError = new Error(error.message);
        loggedError.name = error.name;

        const appInsightException: IExceptionTelemetry = {
          exception: loggedError,
          properties: error.error,
        };

        const appInsightService = this.injector.get(ApplicationInsightsService);
        if (appInsightService.appInsights != null) {
          appInsightService.appInsights.trackException(appInsightException);
        }
        if (!req.context.get(SkipErrorPrompt)) {
          this.errorService.next(error);
        }
        return throwError(() => error);
      }),
    );
  }

}
